import classNames from 'classnames';
import React, { useCallback, useState } from 'react';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { IconTypes } from '@eika/icon';

import { useConfig } from '../../../../defaultConfig';
import usePaymentLimits from '../../../../fetchHooks/usePaymentLimits';
import useReceivers from '../../../../fetchHooks/useReceivers';
import { usePaymentAccountContext } from '../../../../hooks/useAccountState';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { EikaModal } from '../../../EikaModal';
import {
    OwntextInput,
    DateSelect,
    AmountInput,
    ReceiverComboBox,
    FromAccountSelect,
} from '../../../NettbankCreateTransactions/components';
import { MessageInput } from '../../../fields';
import { AccountFreeWithdrawalLabel } from '../../../shared/AccountFreeWithdrawalLabel';
import DesktopDropdownPlaceholder from '../../DesktopDropdownPlaceholder';
import NewReceiver from '../../Payment/SelectReceiver/NewReceiver';
import { SelectedReceiver } from '../../SelectedReceiver';
import { RecurringPaymentActionType } from '../form/actions';
import { amountLimitWarning, isNoCoverage } from '../form/validate';
import FieldProps from './FieldProps';
import usePartialReceiverOcr from './usePartialReceiverOcr';

const RecurringPaymentDesktopFields: React.FC<FieldProps> = ({
    form,
    signingOperationPending,
    locked,
    freeWithdrawals,
    receiverState,
    date,
    validAccounts,
    invalidAccounts,
    accountState,
    shouldFocusFirstElement = false,
}) => {
    const { state, errors, handleChange, curryChange } = form;

    const translated = useTranslation();
    const { data: paymentLimtits } = usePaymentLimits();

    const {
        featureToggles: { disableOwnText },
    } = useConfig();
    const [searchString, setSearchString] = useState('');

    const [showAddReceiver, setShowAddReceiver] = useState(false);

    const [placeholderHeight, setPlaceholderHeight] = useState(0);

    const showLock = locked || signingOperationPending;
    const { data: receivers, ...receiverQuery } = useReceivers();
    const waitingForReceivers = receiverQuery.isLoading || receiverState.isLoading;

    const resetSearchString = useCallback(() => setSearchString(''), [setSearchString]);

    const { ocr, selectReceiver } = usePartialReceiverOcr(handleChange, resetSearchString);

    const getToAccountErrorMessage = () => {
        if (state?.toAccount?.kidRequired) {
            return {
                className: 'transaction-form__to-account-error',
                message: translated.kidRequiredError,
            };
        }

        if (!ocr.error && !errors.toAccount) {
            return undefined;
        }

        const isOcrError = !!ocr.error;
        const message = isOcrError
            ? format(translated.invalidToAccount, ocr.variables?.accountNumber.value ?? '')
            : errors.toAccount;
        return {
            className: 'transaction-form__to-account-error',
            message: message ?? '',
        };
    };

    const { targetFromAccount } = usePaymentAccountContext();

    return (
        <>
            <FromAccountSelect
                className={classNames('transaction-form__from-account-selector open', {
                    error: errors.accountId || errors.toAccount || state?.toAccount?.kidRequired,
                    'has-withdrawal': freeWithdrawals.show,
                })}
                id="recurring-payment-from-account"
                label={translated.from}
                items={validAccounts ?? []}
                invalidItems={invalidAccounts}
                onSelect={curryChange(RecurringPaymentActionType.FROM_ACCOUNT_SELECTED)}
                placeholder={translated.payFromAccount}
                locked={showLock}
                isLoading={accountState.isLoading}
                selected={state.fromAccount}
                selectedAccount={targetFromAccount}
                error={
                    errors.accountId
                        ? { className: 'transaction-form__from-account-error', message: errors.accountId }
                        : undefined
                }
                bottomLabel={
                    freeWithdrawals.show ? (
                        <AccountFreeWithdrawalLabel
                            className="transaction-form__from-account-withdrawal"
                            freeWithdrawals={freeWithdrawals.data}
                        />
                    ) : undefined
                }
                isPaymentModalOpen
                setPlaceholderHeight={setPlaceholderHeight}
                focusFirst={shouldFocusFirstElement}
            />
            <ReceiverComboBox
                className={classNames('transaction-form__to-account-selector open', {
                    error: errors.toAccount || errors.accountId || state?.toAccount?.kidRequired,
                    'has-withdrawal': freeWithdrawals.show,
                })}
                id="recurring-payment-to-account"
                label={translated.to}
                placeholder={translated.addReceiver}
                searchString={
                    searchString.length === 0 && state.toAccount?.accountName
                        ? state.toAccount?.accountName
                        : searchString
                }
                setSearchString={(value: string) => setSearchString(value)}
                icon={IconTypes.PERSONER_24}
                items={receivers ?? []}
                onSelect={(item?: SelectedReceiver) => selectReceiver(item)}
                selected={state.toAccount}
                locked={showLock}
                isLoading={waitingForReceivers || ocr.isPending}
                onShowAddReceiver={() => {
                    setShowAddReceiver(true);
                }}
                addReceiverText={translated.addNewReceiver}
                error={getToAccountErrorMessage()}
                isPaymentModalOpen
                setPlaceholderHeight={setPlaceholderHeight}
            />

            <AmountInput
                className={classNames('transaction-form__amount-selector', {
                    error: errors.amount || errors.amountLimit,
                })}
                value={state.amount}
                label={translated.payAmount}
                error={errors.amount}
                limitError={errors.amountLimit}
                limitWarning={amountLimitWarning(translated, state, paymentLimtits)}
                onChange={curryChange(RecurringPaymentActionType.AMOUNT)}
                isNoCoverage={isNoCoverage(state, targetFromAccount)}
                locked={showLock}
                currency={targetFromAccount?.currency}
            />
            <MessageInput
                className={classNames('transaction-form__message-selector', { error: errors.note })}
                noteValue={state.note}
                error={errors.note}
                selectedReceiver={state.toAccount}
                onChange={curryChange(RecurringPaymentActionType.NOTE)}
                locked={showLock}
                disableKid
            />
            <DateSelect
                className={classNames('transaction-form__date-selector', { error: errors.firstPaymentDate })}
                label="Første trekkdato"
                minDate={date.minDate}
                date={state.firstPaymentDate ?? getZonedDate()}
                onChange={curryChange(RecurringPaymentActionType.FIRST_PAYMENT_DATE)}
                error={errors.firstPaymentDate}
                locked={showLock}
                onlyBusinessDays
                paymentInterval={date.interval}
                onChangePaymentInterval={curryChange(RecurringPaymentActionType.PAYMENT_INTERVAL)}
                endDate={state.lastPaymentDate}
                onSetEndDate={curryChange<ZonedDate | undefined>(RecurringPaymentActionType.LAST_PAYMENT_DATE)}
                customDateFormat="d. MMM yyyy"
            />

            {!disableOwnText && (
                <OwntextInput
                    className="transaction-form__owntext-selector"
                    ownText={state.ownText}
                    locked={showLock}
                    onChangeOwntext={curryChange(RecurringPaymentActionType.OWNTEXT)}
                />
            )}
            <DesktopDropdownPlaceholder height={placeholderHeight} />

            <EikaModal id="payment-add-new-receiver-modal" open={showAddReceiver} setOpen={setShowAddReceiver}>
                <NewReceiver
                    receivers={receivers}
                    searchString={searchString}
                    onChange={(selectedReceiver) => {
                        setShowAddReceiver(false);
                        setSearchString(selectedReceiver.accountName ?? '');
                        handleChange({
                            type: RecurringPaymentActionType.TO_ACCOUNT_SELECTED,
                            payload: selectedReceiver,
                        });
                    }}
                    isNettbank
                    onCancel={() => setShowAddReceiver(false)}
                />
            </EikaModal>
        </>
    );
};

export default RecurringPaymentDesktopFields;
